import { LogOut, User2Icon, Bell, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRef, useEffect, useState, useMemo } from "react";
import avatar4 from "../../assets/img/avatar4.png";
import jpFrag from "../../assets/icons/japanflag.svg";
import usFrag from "../../assets/icons/usaflag.svg";
import idFrag from "../../assets/icons/indonesiaflag.svg";
import logo from "../../assets/logo/evocare.svg";
import DropdownWithModal from "../ui/DropDownModal";
import { getProfile } from "../../api/user/userService";
import { logout } from "../../api/login/authService";
import { useNavigate } from "react-router-dom";
import { User } from "../../types/user";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../api/notifications/notifications";
import { Notification } from "../../types/notification";

interface IHeaderProps {
  isOpenSlide: boolean;
}

const Header: React.FC<IHeaderProps> = ({ isOpenSlide }) => {
  const [lang, setLang] = useState("en");
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const languages = [
    { code: "en", label: "English", flag: usFrag },
    { code: "ja", label: "日本語", flag: jpFrag },
    { code: "id", label: "Bahasa Indonesia", flag: idFrag },
  ];
  const { t, i18n } = useTranslation();
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const fetchProfileRef = useRef(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isNotifModalMounted, setIsNotifModalMounted] = useState(false);
  const [isNotifModalVisible, setIsNotifModalVisible] = useState(false);

  // Pagination for notifications
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(1);

  // always keep pageRef updated
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const openNotifModal = () => {
    setIsNotifModalMounted(true);
    setTimeout(() => setIsNotifModalVisible(true), 10);
  };

  const closeNotifModal = () => {
    setIsNotifModalVisible(false);
    setTimeout(() => setIsNotifModalMounted(false), 300);
  };

  const buttons = useMemo(
    () => [
      {
        label: t("title.yourProfile"),
        icon: <User2Icon />,
        onClick: () => navigate("/profile"),
      },
      {
        label: t("title.logout"),
        icon: <LogOut />,
        onClick: () => handleLogout(),
      },
    ],
    [t]
  );

  const handleLogout = async () => {
    await logout();
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    setLang(lang);
    setIsLangDropdownOpen(false);
  };

  const fetchNotifications = async (_page = 1, append = false) => {
    try {
      const res = await getNotifications(_page);
      const newNotifs = Array.isArray(res) ? res : [];
      setHasMore(newNotifs.length > 0);
      if (append) {
        setNotifications((prev) => [...prev, ...newNotifs]);
      } else {
        setNotifications(newNotifs);
      }
    } catch (err) {
      if (!append) setNotifications([]);
      setHasMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications(page);

    const intervalId = setInterval(
      () => {
        fetchNotifications(page);
      },
      1 * 60 * 1000
    );

    return () => {
      clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    if (!isLangDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  useEffect(() => {
    setLang(localStorage.getItem("language") || "en");
    setIsMounted(true);

    if (fetchProfileRef.current) return;
    fetchProfileRef.current = true;

    const fetchUserProfile = async () => {
      try {
        const profileData = await getProfile();
        localStorage.setItem("profile", JSON.stringify(profileData));
        setUser(profileData);
      } catch (err: any) {
        console.error("Error fetching user profile:", err);
        if (err?.response?.status === 401) {
          const { logout } = await import("../../api/login/authService");
          logout();
        }
      }
    };
    fetchUserProfile();
  }, []);

  if (!isMounted || !i18n.isInitialized) return null;

  const unreadCount = notifications.filter(
    (notif) => notif.isRead === false
  ).length;

  const handleNotificationClick = async (notif: Notification) => {
    try {
      await markNotificationAsRead(String(notif.id));
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n))
      );
      navigate("/consultation");
    } catch (err) {
      console.log("Error marking notification as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err) {
      console.log("Error marking all notifications as read:", err);
    }
  };

  // Load more notifications
  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    await fetchNotifications(page + 1, true);
    setPage((prev) => prev + 1);
    setIsLoadingMore(false);
  };

  function getNotificationContent(notif: Notification, t: any) {
    switch (notif.type) {
      case "PATIENT_BOOKING_CREATED":
        return {
          title: t("title.bookingCreated"),
          message: t("title.bookingMessage"),
        };
      case "PATIENT_BOOKING_CANCELLED":
        return {
          title: t("title.cancelled"),
          message: t("title.cancelledMessage"),
        };
      case "PATIENT_BOOKING_APPROVED":
        return {
          title: t("title.approved"),
          message: t("title.approvedMessage"),
        };
      case "PATIENT_BOOKING_DECLINED":
        return {
          title: t("title.declined"),
          message: t("title.declinedMessage"),
        };
      default:
        return {
          title: notif.title,
          message: notif.message,
        };
    }
  }

  return (
    <header
      className={`${
        isOpenSlide ? "left-[139px]" : "left-0"
      } fixed  top-0 right-0 left-0 flex items-center gap-2 bg-white 
    shadow-xs rounded-lg md:px-4 md:py-3 z-20 transition-all duration-300 ease-in-out p-2`}
    >
      <div className="flex flex-col md:flex-wrap text-[24px] text-gray-900">
        <div className="relative flex items-center w-auto mb-2">
          <img src={logo} alt="Evocare Logo" width={200} height={48} />
        </div>
        <div className="flex flex-col md:flex-row relative w-auto">
          <strong className="font-bold-700">{t("header.welcome")},</strong>
          <div>{user ? ` ${user.firstName} ${user.lastName}` : ""}</div>
        </div>
      </div>

      <div className="ml-auto flex items-center justify-center gap-4 mr-2">
        <button
          className="relative flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 shadow hover:bg-orange-100 transition"
          onClick={openNotifModal}
          title={t("title.notifications")}
        >
          <Bell size={28} className="text-orange-700" />
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse" />
          )}
        </button>

        <div ref={langDropdownRef} className="relative hidden md:block">
          <button
            type="button"
            className="flex items-center w-50 h-10 bg-white border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-200 shadow-md cursor-pointer hover:bg-orange-50 transition-all duration-300 ease-in-out"
            onClick={() => setIsLangDropdownOpen((prev) => !prev)}
          >
            <img
              src={languages.find((l) => l.code === lang)?.flag || usFrag}
              alt="Flag"
              width={30}
              height={30}
              className={`inline-block mr-2 align-middle ${lang === "id" ? "rounded-full border border-gray-200" : ""}`}
            />
            {/* <span className="mr-2">{languages.find(l => l.code === lang)?.label}</span> */}
            <svg
              className="w-4 h-4 ml-auto"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {isLangDropdownOpen && (
            <div className="absolute left-0 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-30">
              {languages.map((l) => (
                <button
                  key={l.code}
                  className={`flex items-center w-full px-3 py-2 hover:bg-orange-50 ${lang === l.code ? "bg-orange-100" : ""}`}
                  onClick={() => changeLanguage(l.code)}
                >
                  <img
                    src={l.flag}
                    alt={l.label + " flag"}
                    width={22}
                    height={22}
                    className={`inline-block mr-2 align-middle ${l.code === "id" ? "rounded-full border border-gray-200" : ""}`}
                  />
                  {/* <span>{l.label}</span> */}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative hidden md:block">
          <button
            ref={triggerRef}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            <img
              src={avatar4}
              alt="User Avatar"
              width={40}
              height={40}
              className="rounded-full cursor-pointer"
            />
          </button>

          <DropdownWithModal
            buttons={buttons}
            setIsDropdownOpen={setIsDropdownOpen}
            isDropdownOpen={isDropdownOpen}
            triggerRef={triggerRef}
            title={t("title.moreAction")}
          />
        </div>
      </div>
      {isNotifModalMounted && (
        <div
          className={`
            fixed top-0 right-0 w-[380px] md:w-[450px] bg-white shadow-lg z-50 rounded-lg
            transition-all duration-300
            ${
              isNotifModalVisible
                ? "opacity-100 translate-x-0 pointer-events-auto"
                : "opacity-0 translate-x-full pointer-events-none"
            }
          `}
          style={{ maxHeight: "92vh" }}
        >
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <span className="font-semibold text-lg text-orange-600">
              {t("title.notifications")}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="text-orange-600 hover:bg-orange-100 px-3 py-1 rounded text-sm border border-orange-300"
                onClick={handleMarkAllAsRead}
              >
                {t("title.markAllAsRead")}
              </button>
              <button
                className="text-gray-500 hover:text-orange-600 w-6"
                onClick={closeNotifModal}
              >
                <X size={24} />
              </button>
            </div>
          </div>
          <div
            className="p-4 overflow-y-auto"
            style={{ maxHeight: "calc(92vh - 56px)" }}
          >
            {notifications.length === 0 ? (
              <div className="text-gray-500">{t("title.noNotifications")}</div>
            ) : (
              <>
                {notifications.map((notif) => {
                  const content = getNotificationContent(notif, t);
                  const meta =
                    Array.isArray(notif.metadata) && notif.metadata.length > 0
                      ? notif.metadata[0]
                      : null;
                  let timeRange = "";
                  if (meta) {
                    const start = new Date(meta.startTime);
                    const end = new Date(meta.endTime);
                    const startStr = start.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });
                    const endStr = end.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });
                    timeRange = `${startStr} - ${endStr}`;
                  }
                  return (
                    <div
                      key={notif.id}
                      className="mb-4 p-3 rounded bg-orange-50 border-l-4 border-orange-300 relative cursor-pointer hover:bg-orange-100 transition"
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <div className="font-semibold text-gray-700">
                        {content.title}
                      </div>
                      <div className="text-gray-700 text-sm">
                        {content.message}
                      </div>
                      {meta && (
                        <div className="mt-2 text-xs text-gray-600">
                          <div>
                            <b>{t("title.title")}:</b>{" "}
                            <span style={{ wordBreak: "break-all" }}>
                              {meta.title}
                            </span>
                          </div>
                          <div>
                            <b>{t("title.time")}:</b> {timeRange}
                          </div>
                        </div>
                      )}
                      <div className="text-gray-400 text-xs mt-1">
                        {new Date(notif.createdAt).toLocaleString()}
                      </div>
                      {notif.isRead === false && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-orange-500 rounded-full" />
                      )}
                    </div>
                  );
                })}
                {hasMore && (
                  <button
                    className="w-full py-2 mt-2 border rounded text-orange-600 bg-orange-50 hover:bg-orange-100 disabled:opacity-60"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? t("login.loading") : t("title.loadMore")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
